import { useCallback, useEffect, useState } from "react";
import Carregando from "../componentes/Carregando";
import { LogoCF } from "../componentes/ui";
import { supabase } from "../lib/supabase";
import {
  extrato,
  faturamento,
  lancar,
  minhaConta,
  motivoDoTeto,
  type FaturamentoAno,
  type Lancamento,
  type MinhaContaMei,
} from "../lib/meipro";
import { BarraDoTeto, FormularioDeLancamento, LinhaDeLancamento } from "./MeiGestao";
import { CAMPO } from "../componentes/ui";

// A tela do CLIENTE MEI — o segundo público do hub (decisão de Victor, 31/07:
// "pode logar assim que o aluno o cadastra. Acesso LIMITADO — não vê o sistema,
// NÃO é aluno").
//
// Por isso ela NÃO usa o Topo do aluno: Início, Faculdade e Perfil são portas de
// quem comprou curso, e cada uma delas aqui seria um convite para uma tela que
// responderia "você não tem nada". O cabeçalho é o da página pública do
// orçamento — a marca, o nome, e a saída.
//
// As peças do livro (barra do teto, formulário, linha do extrato) são AS MESMAS
// da tela do contador, importadas de MeiGestao: duas versões do mesmo livro
// divergiriam na primeira reescrita, e o cliente veria um saldo diferente do
// que o contador vê — que é o defeito que ninguém consegue depurar depois.

/**
 * O envelope que pergunta ao banco "esta conta é de um cliente MEI?" e decide.
 * Usado em DOIS lugares: a tela inicial (quando o login não tem curso nenhum) e
 * a rota direta /meu-livro (para quem é aluno E cliente ao mesmo tempo). O
 * `quandoNaoCliente` é de quem chama, porque cada lugar tem a resposta certa —
 * a inicial explica o prazo vencido, a rota direta volta para o início.
 */
export function MeuLivroPorConta({ quandoNaoCliente }: { quandoNaoCliente: React.ReactNode }) {
  const [conta, setConta] = useState<MinhaContaMei | null | undefined>(undefined);
  useEffect(() => {
    let ativo = true;
    void minhaConta().then((c) => {
      if (ativo) setConta(c);
    });
    return () => {
      ativo = false;
    };
  }, []);

  if (conta === undefined) return <Carregando />;
  if (conta?.sou_cliente) return <MeuLivro conta={conta} />;
  return <>{quandoNaoCliente}</>;
}

export default function MeuLivro({ conta }: { conta: MinhaContaMei }) {
  const clienteId = conta.cliente_id!;
  const [anos, setAnos] = useState<FaturamentoAno[] | null>(null);
  const [ano, setAno] = useState<number>(new Date().getFullYear());
  const [linhas, setLinhas] = useState<Lancamento[] | null>(null);
  const [lancando, setLancando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(async () => {
    const [fat, ext] = await Promise.all([faturamento(clienteId), extrato(clienteId, ano)]);
    if (fat === null || ext === null) setErro(true);
    else {
      setAnos(fat);
      setLinhas(ext);
    }
  }, [clienteId, ano]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const anoCorrente = new Date().getFullYear();
  const contadores = conta.contadores.map((c) => c.nome).filter(Boolean);

  return (
    <div className="flex min-h-dvh flex-col bg-fundo">
      <header className="bg-azul">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <LogoCF className="h-7" />
          <button
            type="button"
            className="text-sm font-semibold text-navy-mute hover:text-white"
            onClick={() => void supabase.auth.signOut()}
          >
            Sair
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6">
        <h1 className="font-display text-2xl font-bold text-titulo">Meu livro-caixa</h1>
        {contadores.length > 0 && (
          <p className="mt-0.5 text-sm text-cinza">
            {contadores.length === 1
              ? `Seu contador: ${contadores[0]}`
              : `Seus contadores: ${contadores.join(", ")}`}
          </p>
        )}

        {erro ? (
          <p className="mt-8 rounded-xl bg-superficie-2 p-6 text-center text-sm leading-relaxed text-cinza">
            Não conseguimos carregar o seu livro agora. Recarregue a página; se continuar,
            fale com o seu contador.
          </p>
        ) : anos === null || linhas === null ? (
          <div className="py-16">
            <Carregando />
          </div>
        ) : (
          <>
            <section aria-labelledby="meu-teto" className="surface mt-6 p-4 sm:p-5">
              <h2 id="meu-teto" className="font-display text-base font-bold text-titulo">
                Faturamento × teto do MEI
              </h2>
              {(() => {
                const doCorrente = anos.find((a) => a.ano === anoCorrente);
                return (
                  <BarraDoTeto
                    rotulo={`Receita em ${anoCorrente}`}
                    valor={doCorrente?.receita ?? 0}
                    // Sem lançamento no ano corrente, `mei_faturamento` não
                    // devolve linha nenhuma para ele — e a versão anterior caía
                    // no teto do ANO MAIS RECENTE COM LANÇAMENTO, ou em zero.
                    // O teto do ano corrente é da CONTA (000091), que sempre
                    // responde.
                    teto={doCorrente?.limite ?? conta.limite}
                    // O cliente MEI é o usuário mais leigo do hub: ver 47.250
                    // onde ele ouviu falar de 81 mil, sem explicação, parece
                    // erro. A frase vem do banco, igual à do contador.
                    motivo={
                      doCorrente
                        ? motivoDoTeto({
                            manual: doCorrente.teto_manual,
                            caminhoneiro: doCorrente.caminhoneiro,
                            mesDeAberturaProporcional: doCorrente.mes_de_abertura,
                          })
                        : motivoDoTeto({
                            manual: conta.teto_manual,
                            caminhoneiro: conta.teto_caminhoneiro,
                            mesDeAberturaProporcional: conta.teto_mes_abertura,
                          })
                    }
                  />
                );
              })()}
            </section>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-cinza">
                Ano
                <select
                  className={CAMPO + " w-auto"}
                  value={ano}
                  onChange={(e) => setAno(Number(e.target.value))}
                >
                  {[...new Set([anoCorrente, ...anos.map((a) => a.ano)])]
                    .sort((a, b) => b - a)
                    .map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                </select>
              </label>
              <button type="button" className="pill-cta" onClick={() => setLancando((v) => !v)}>
                {lancando ? "Fechar" : "+ Novo lançamento"}
              </button>
            </div>

            {lancando && (
              <FormularioDeLancamento
                aoSalvar={async (d) => {
                  const r = await lancar(clienteId, d);
                  return r.ok ? { ok: true, id: r.res } : r;
                }}
                aoConcluir={(a) => {
                  setLancando(false);
                  setAviso(a ?? null);
                  void carregar();
                }}
              />
            )}

            {aviso && <p className="mt-3 text-sm font-semibold text-alerta-tinta">{aviso}</p>}

            {linhas.length === 0 ? (
              <div className="surface mt-4 p-8 text-center">
                <p className="text-sm leading-relaxed text-cinza">
                  Nenhum lançamento em {ano}. Registre as suas entradas e despesas — o seu
                  contador vê o mesmo livro.
                </p>
              </div>
            ) : (
              <ul className="mt-4 space-y-2">
                {linhas.map((l) => (
                  <LinhaDeLancamento key={l.id} lancamento={l} aoMudar={carregar} />
                ))}
              </ul>
            )}
          </>
        )}
      </main>

      <footer className="bg-azul py-5 text-center">
        <p className="text-xs text-navy-mute">
          Livro-caixa oferecido pelo seu contador, com a Contabilidade Facilitada.
        </p>
      </footer>
    </div>
  );
}
